import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, Mail, Facebook, Instagram, Twitter, 
  Linkedin, Send, Award, Shield, CreditCard 
} from 'lucide-react';

const Footer = ({ language = 'de', setCurrentPage }) => {
  const translations = {
    de: {
      quickLinks: 'Schnelle Links',
      contact: 'Kontakt',
      rooms: 'Zimmer',
      menu: 'Menü',
      gallery: 'Erleben Sie mehr...',
      events: 'Veranstaltungen',
      about: 'Über uns',
      contactInfo: 'Kontakt Info',
      newsletter: 'Newsletter',
      newsletterText: 'Bleiben Sie über unsere neuesten Angebote auf dem Laufenden',
      subscribe: 'Abonnieren',
      emailPlaceholder: 'Ihre E-Mail-Adresse',
      description: 'Charmantes Familienhotel in Hattingen, NRW mit authentischem italienischem Restaurant La Casa.',
      copyright: 'Alle Rechte vorbehalten.',
      imprint: 'Impressum',
      dataProtection: 'Datenschutz',
      terms: 'AGB'
    },
    en: {
      quickLinks: 'Quick Links',
      contact: 'Contact',
      rooms: 'Rooms',
      menu: 'Menu',
      gallery: 'Explore more...',
      events: 'Events',
      about: 'About',
      contactInfo: 'Contact Info',
      newsletter: 'Newsletter',
      newsletterText: 'Stay updated with our latest offers',
      subscribe: 'Subscribe',
      emailPlaceholder: 'Your email address',
      description: 'Charming family hotel in Hattingen, NRW with authentic Italian restaurant La Casa.',
      copyright: 'All rights reserved.',
      imprint: 'Imprint',
      dataProtection: 'Data Protection',
      terms: 'Terms & Conditions'
    }
  };

  const t = translations[language];
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log('Newsletter subscription:', email);
    alert('Thank you for subscribing!');
    setEmail('');
  };

  return (
    <footer id="contact" className="bg-luxury-charcoal text-white">
      {/* Main Footer */}
      <div className="section-padding border-b border-luxury-gold/20">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div>
              <h3
                className="text-3xl font-display font-bold text-gradient mb-4"
              >
                Hotel La Casa
              </h3>
              <p className="text-luxury-cream mb-6 leading-relaxed">
                {t.description}
              </p>
              <div className="flex space-x-4">
                {[
                  { Icon: Facebook, href: '#' },
                  { Icon: Instagram, href: '#' },
                  { Icon: Twitter, href: '#' },
                  { Icon: Linkedin, href: '#' },
                ].map(({ Icon, href }, index) => (
                  <a
                    key={index}
                    href={href}
                    className="bg-luxury-slate hover:bg-luxury-gold text-white p-3 rounded-full transition-colors"
                  >
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xl font-display font-bold mb-6 text-luxury-gold">
                {t.quickLinks}
              </h4>
              <ul className="space-y-3">
                {[
                  { label: t.rooms, href: '#rooms' },
                  { label: t.menu, href: '#menu' },
                  { label: t.gallery, href: '#gallery' },
                  { label: t.events, href: '#events' },
                  { label: t.about, href: '#about' },
                  { label: t.contact, href: '#', onClick: () => setCurrentPage && setCurrentPage('contact') },
                  { label: 'Impressum', href: '/impressum' },
                  { label: 'Datenschutz', href: '/datenschutz' },
                ].map((link, index) => (
                  <li key={index}>
                    {link.onClick ? (
                      <button
                        onClick={link.onClick}
                        className="text-luxury-cream hover:text-luxury-gold transition-colors text-left"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a
                        href={link.href}
                        className="text-luxury-cream hover:text-luxury-gold transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-xl font-display font-bold mb-6 text-luxury-gold">
                {t.contactInfo}
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <MapPin className="text-luxury-gold flex-shrink-0 mt-1" size={20} />
                  <span className="text-luxury-cream">
                    Wodental 4<br />
                    45529 Hattingen<br />
                    Deutschland
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <Phone className="text-luxury-gold flex-shrink-0" size={20} />
                  <a href="tel:+49232456650" className="text-luxury-cream hover:text-luxury-gold transition-colors">
                    +49232456650
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail className="text-luxury-gold flex-shrink-0" size={20} />
                  <a href="mailto:info@lacasa-hotel.de" className="text-luxury-cream hover:text-luxury-gold transition-colors">
                    info@lacasa-hotel.de
                  </a>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-xl font-display font-bold mb-6 text-luxury-gold">
                {t.newsletter}
              </h4>
              <p className="text-luxury-cream mb-4">
                {t.newsletterText}
              </p>
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailPlaceholder}
                    required
                    className="w-full px-4 py-3 bg-luxury-slate border border-luxury-gold/30 rounded-lg focus:border-luxury-gold focus:outline-none text-white placeholder-gray-400"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full btn-primary rounded-lg py-3 flex items-center justify-center space-x-2"
                >
                  <span>{t.subscribe}</span>
                  <Send size={18} />
                </motion.button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="py-8 border-b border-luxury-gold/20">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center items-center gap-8">
            {[
              { Icon: Award, text: 'Award Winning' },
              { Icon: Shield, text: 'Secure Booking' },
              { Icon: CreditCard, text: 'Safe Payment' },
            ].map(({ Icon, text }, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -3 }}
                className="flex items-center space-x-2 text-luxury-cream"
              >
                <Icon className="text-luxury-gold" size={24} />
                <span className="font-semibold">{text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="py-6">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              <p className="text-luxury-cream text-sm">
                © {new Date().getFullYear()} Hotel La Casa & Ristorante La Casa. {t.copyright}
              </p>
              <span className="hidden md:inline text-luxury-gold">|</span>
              <p className="text-luxury-cream/60 text-xs">
                Powered by <a href="https://saninno.de" target="_blank" rel="noopener noreferrer" className="text-luxury-gold hover:text-white transition-colors font-semibold">SanINNO UG</a>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <a href="/impressum" className="text-luxury-cream hover:text-luxury-gold transition-colors font-semibold">
                {t.imprint}
              </a>
              <a href="/datenschutz" className="text-luxury-cream hover:text-luxury-gold transition-colors font-semibold">
                {t.dataProtection}
              </a>
              <a href="#agb" className="text-luxury-cream hover:text-luxury-gold transition-colors">
                {t.terms}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
